import { SpinnerButton } from "components/shared/SpinnerButton";
import { combine, isEmail, required } from "shared/validations/validations";
import { ResendSignUpCodeError } from "components/user/ResendSignUpCodeError";
import { toastActions } from "domain/toast/toastActions";
import { resendSignUpCode } from "domain/user/resendSignUpCode";
import { ResendLicensingSignUpCodeError, resendSignUpCodeMutationResponse } from "generatedQueries/resendSignUpCodeMutation.graphql";
import { parse } from "query-string";
import React, { FC, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { useRelayEnvironment } from "react-relay";
import { useLocation } from "react-router";
import { Col, Form, Row } from "reactstrap";
import { PayloadError } from "relay-runtime";
import { FormLabel } from "shared/components/formInputs/FormLabel";
import { TextBox } from "shared/components/newFormInputs/TextBox";
import { mapMessages } from "shared/mapMessages";
import { FormProvider, useForm } from "react-hook-form";

export const messages = mapMessages("components.user.resendsignup", {
    emailAddressLabel: "Email",
    resendButton: "Send",
    signUpCodeResentToast: "Sign up code has been sent, please check your email to continue.",
});

interface IQueryString {
    email?: string;
}

interface IResendSignUpCode {
    email: string;
}

export const ResendSignUpCode: FC = () => {
    const location = useLocation();
    const environment = useRelayEnvironment();
    const { formatMessage } = useIntl();
    const dispatch = useDispatch();
    const [failure, setFailure] = useState<boolean | ResendLicensingSignUpCodeError>(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const queryString = parse(location.search) as IQueryString;
    const methods = useForm<IResendSignUpCode>({
        defaultValues: {
            email: queryString.email || "",
        },
    });

    useEffect(() => {
        if (failure) {
            methods.setFocus("email", { shouldSelect: true });
        }
    }, [failure]);

    return (
        <Row>
            <Col>
                <FormProvider {...methods}>
                    <Form onSubmit={methods.handleSubmit(resend)} role="form" noValidate={true}>
                        <FormLabel
                            message={messages.emailAddressLabel}
                            for="resendemail"
                            required={true}
                        >
                            <TextBox
                                id="resendemail"
                                name="email"
                                type="email"
                                register={methods.register}
                                control={methods.control}
                                placeholder={formatMessage(messages.emailAddressLabel)}
                                options={combine<IResendSignUpCode, "email">(
                                    required(),
                                    isEmail()
                                )}
                                aria-required={true} />
                        </FormLabel>
                        <ResendSignUpCodeError error={failure} />
                        <SpinnerButton className="float-right" color="primary" isLoading={isSubmitting} message={messages.resendButton} />
                    </Form>
                </FormProvider>
            </Col>
        </Row>
    );

    function resend({ email }: { email: string }) {
        setIsSubmitting(true);
        resendSignUpCode(
            environment,
            email,
            onResendCompleted,
            onResendError);
    }

    function onResendCompleted(response: resendSignUpCodeMutationResponse, errors?: PayloadError[] | null) {
        setIsSubmitting(false);
        if (errors?.length || !response.resendLicensingSignUpCode?.wasSuccessful) {
            setFailure(response.resendLicensingSignUpCode?.errors?.[0]?.errorCode ?? true);
        } else {
            dispatch(toastActions.customSuccessToast(messages.signUpCodeResentToast));
        }
    }

    function onResendError() {
        setIsSubmitting(false);
        setFailure(true);
    }
};
